const RETRY_DELAYS = [1500, 5000, 15000];
const TRANSIENT = new Set(['unavailable', 'deadline-exceeded', 'aborted', 'internal', 'unknown']);
const ACCESS = new Set(['permission-denied', 'unauthenticated']);
export const studyErrorCode = error => String(error?.code || 'unknown').replace(/^(?:firestore|functions)\//, '');
export function studyErrorMessage(code) {
    if (ACCESS.has(code)) return 'Your study access could not be confirmed. Retry, or contact your coordinator if this continues.';
    if (code === 'resource-exhausted') return 'The study service has reached a usage limit. Please try again later or contact your coordinator.';
    if (code === 'failed-precondition') return 'The study service needs an administrator’s attention. Please share the error reference below with your coordinator.';
    return 'The study service could not be reached. Check your connection and retry.';
}

// A failed Firestore listener is terminal. Restart only failed subscriptions,
// keeping successful lists and protecting against callbacks from an old account.
export function createStudyDataLoader(sources, {onData, onError, onLoading, recoverAccess = async () => {}}) {
    let active = true, accessRecovery = null, accessRecovered = false;
    const slots = Object.fromEntries(Object.keys(sources).map(name => [name, {version:0, stop:null, timer:null, error:null, attempts:0}]));
    const stop = slot => { clearTimeout(slot.timer); slot.timer = null; slot.stop?.(); slot.stop = null; };
    const connect = name => {
        const slot = slots[name]; stop(slot);
        const version = ++slot.version; let ended = false;
        const current = () => active && slot.version === version;
        onLoading(name);
        const failed = error => {
            if (!current() || ended) return;
            ended = true; slot.error = studyErrorCode(error); stop(slot); onError(name, slot.error);
            if (ACCESS.has(slot.error) && !accessRecovered) {
                // Coalesce simultaneous exam/history failures into one recovery.
                if (!accessRecovery) accessRecovery = Promise.resolve().then(() => recoverAccess()).then(value => value !== false, () => false).then(ok => {
                    accessRecovered = true; accessRecovery = null;
                    if (active && ok) for (const [key, state] of Object.entries(slots)) if (ACCESS.has(state.error)) connect(key);
                });
            } else if (TRANSIENT.has(slot.error) && slot.attempts < RETRY_DELAYS.length) {
                const delay = RETRY_DELAYS[slot.attempts++];
                slot.timer = setTimeout(() => { slot.timer = null; if (current()) connect(name); }, delay);
            }
        };
        try {
            const unsubscribe = sources[name](data => {
                if (!current() || ended) return;
                slot.error = null; slot.attempts = 0; onData(name, data);
            }, failed);
            if (!current() || ended) unsubscribe?.(); else slot.stop = unsubscribe;
        } catch (error) { failed(error); }
    };
    const retry = (manual = false) => {
        if (!active) return;
        if (manual && !accessRecovery) accessRecovered = false;
        for (const [name, slot] of Object.entries(slots)) if (slot.error) {
            if (accessRecovery && ACCESS.has(slot.error)) continue;
            if (manual) slot.attempts = 0;
            // Do not poll quota/configuration errors automatically.
            if (manual || ACCESS.has(slot.error) || TRANSIENT.has(slot.error)) connect(name);
        }
    };
    for (const name of Object.keys(sources)) connect(name);
    return {retry, dispose:() => { active = false; for (const slot of Object.values(slots)) { slot.version++; stop(slot); } }};
}
